import { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";

/**
 * Performance against potential.
 *
 * Rows are potential (High at the top, reading like every nine-box people have
 * seen); columns are the performance third the API computed. The band maths
 * lives on the server so a company on a 1-10 scale lands in the same cells
 * here as it does in an export.
 *
 * People with no potential rating are NOT plotted. "Not assessed" and "low
 * potential" are different claims, and a grid that quietly merges them labels
 * someone as low-potential because their manager skipped a dropdown.
 */
const CELL_LABELS = {
    "3-1": "Enigma", "3-2": "Growth Employee", "3-3": "Future Leader",
    "2-1": "Inconsistent", "2-2": "Core Employee", "2-3": "High Impact",
    "1-1": "Risk", "1-2": "Effective", "1-3": "Trusted Professional",
};

const CELL_TONE = {
    "3-3": "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900/40",
    "3-2": "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40",
    "2-3": "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40",
    "2-2": "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40",
    "3-1": "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40",
    "1-3": "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40",
    "2-1": "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40",
    "1-2": "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40",
    "1-1": "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/40",
};

const POTENTIAL_LABEL = { 3: "High potential", 2: "Medium potential", 1: "Low potential" };
const PERFORMANCE_LABEL = { 1: "Below", 2: "Meets", 3: "Exceeds" };

const NineBoxGrid = ({ plotted = [], awaitingPotential = [], linkBase = "/appraisal/team-review" }) => {
    const navigate = useNavigate();

    const cells = useMemo(() => {
        const map = {};
        for (const p of plotted) {
            const key = `${p.potential_rating}-${p.performance_band}`;
            (map[key] = map[key] || []).push(p);
        }
        return map;
    }, [plotted]);

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                    <div className="grid grid-cols-[auto_repeat(3,1fr)] gap-2">
                        <div />
                        {[1, 2, 3].map((perf) => (
                            <div key={perf} className="text-center text-xs font-medium text-muted-foreground pb-1">
                                {PERFORMANCE_LABEL[perf]} performance
                            </div>
                        ))}

                        {[3, 2, 1].map((pot) => (
                            <Fragment key={pot}>
                                <div
                                    className="flex items-center text-xs font-medium text-muted-foreground pr-2 whitespace-nowrap">
                                    {POTENTIAL_LABEL[pot]}
                                </div>
                                {[1, 2, 3].map((perf) => {
                                    const key = `${pot}-${perf}`;
                                    const people = cells[key] || [];
                                    return (
                                        <div key={key}
                                            className={cn("rounded-lg border p-2 min-h-[104px]",
                                                CELL_TONE[key] || "bg-gray-50 dark:bg-gray-800/40 border-border")}>
                                            <div className="text-[11px] font-semibold text-muted-foreground mb-1">
                                                {CELL_LABELS[key]}
                                                <span className="ml-1 font-normal">({people.length})</span>
                                            </div>
                                            <div className="space-y-1">
                                                {people.map((p) => (
                                                    <button
                                                        key={p.appraisal_id}
                                                        type="button"
                                                        onClick={() => navigate(`${linkBase}/${p.appraisal_id}`)}
                                                        className="block w-full text-left text-xs bg-white/70 dark:bg-gray-900/40 rounded px-1.5 py-1 hover:bg-white dark:hover:bg-gray-900"
                                                        title={`${p.final_rating || ""} · ${p.final_score}`}
                                                    >
                                                        <span className="font-medium">{p.employee?.name}</span>
                                                        {p.is_calibrated && (
                                                            <span className="ml-1 text-[10px] text-violet-600 dark:text-violet-400">
                                                                calibrated
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {awaitingPotential.length > 0 && (
                <div className="rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                        {awaitingPotential.length} not plotted — no potential rating
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 mb-2">
                        These are left off the grid rather than placed in the bottom row.
                        The reporting manager sets potential when approving the review.
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {awaitingPotential.map((p) => (
                            <button key={p.appraisal_id} type="button"
                                onClick={() => navigate(`${linkBase}/${p.appraisal_id}`)}
                                className="text-xs bg-white dark:bg-gray-900/50 border border-border rounded px-2 py-0.5 hover:bg-gray-50">
                                {p.employee?.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NineBoxGrid;
